import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {UserRepository, UserSearchResult} from '@app/@core/repositories/user.repository';
import {AppService} from '@shared/services/app.service';
import {DeviceDetectorService} from 'ngx-device-detector';
import {formly} from '@shared/helper/formly-helpers';
import {UserNoteRepository} from '@app/@core/repositories/be3/user-note.repository';
import {UserNote} from '@app/models/v3/user-note';
import {UserModel} from '@app/models/user';
import User = UserModel.User;

@Component({
  selector: 'app-user-card',
  template: `
    <div *ngIf="showSelect"
         class="mb-4">
      <i class="pi pi-user mr-3"></i>
      <app-user-select (selected)="userSelected($event)"></app-user-select>
    </div>
    <div *ngIf="user"
         class="w-full surface-card shadow-2 border-round-2xl p-3 flex flex-column md:flex-row gap-3 align-items-start">
      <!-- User Image -->
      <img [src]="user?.photo || defaultPhoto"
           alt="User Image"
           class="w-4rem h-4rem border-circle object-cover pointer"
           (click)="goToUser()"/>

      <!-- Info Section -->
      <div class="flex-1 flex gap-5">
        <!-- Name & Contact -->
        <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-2">
          <div>
            <div class="font-medium pointer"
                 (click)="goToUser()">{{ user.fname }} {{ user.lname }}
            </div>
            <div class="text-sm text-color-secondary mt-2">
              <i class="pi pi-envelope mr-1"></i>{{ user.email }}
            </div>
            <div class="text-sm text-color-secondary mt-2">
              <i class="pi pi-phone mr-1"></i>{{ user.mobile }}
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div class="mr-2">
          <ul class="m-0 pl-3 text-sm text-700"
              style="list-style-type: disc;">
            <li *ngFor="let note of notes">
              {{ note.content }}
              <span class="text-xs text-500 ml-2">({{ note.updated | date:'yyyy-MM-dd' }})</span>
            </li>
            <li>
              <div class="text-green-500 pointer underline"
                   (click)="openAddNoteDialog()">
                + Add new note...
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  `,
  styles: []
})
export class UserCardComponent implements OnChanges {
  @Input() userId: string;
  @Input() showSelect = false;
  @Output() userChanged = new EventEmitter<User>();

  user: User;

  defaultPhoto = 'assets/images/no-profile-image.png';

  isMobile = false;
  notes: UserNote[] = [];

  constructor(private userRepository: UserRepository,
              private deviceDetectorService: DeviceDetectorService,
              private userNoteRepository: UserNoteRepository,
              private app: AppService) {
    this.isMobile = this.deviceDetectorService.isMobile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.userId) {
      this.userRepository.get(this.userId).subscribe({
        next: user => {
          this.user = user;
          this.loadNotes();
        }
      });
    }
  }

  goToUser() {
    this.app.navigateToUser(this.userId);
  }

  userSelected(userSearchResult: { id: string }) {
    if (userSearchResult?.id) {
      this.userRepository.get(userSearchResult?.id).subscribe({
        next: user => {
          this.user = user;
          this.userChanged.emit(user);
          this.loadNotes();
        }
      });
    }
  }

  openAddNoteDialog() {
    const formlyFields = [
      formly.requiredText('note', 'Note')
    ];

    this.app.showInputDialog('Add Note', formlyFields, {}, '800px', '400px').onClose.subscribe(ok => {
      if (ok && ok.note) {
        this.userNoteRepository.create({
          userId: this.userId,
          content: ok.note
        }).subscribe(() => {
          this.app.toastSuccess('Note added successfully');
          this.loadNotes();
        });
      }
    });
  }

  private loadNotes() {
    this.userNoteRepository.findByUserId(this.userId).subscribe(notes => {
      this.notes = notes;
    });
  }
}
